import { useAuth0 } from '@auth0/auth0-react';

import { getApiAccountProfile, ProfileResponse } from '@/api';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import { log } from '@/utils/functions/logger/logger';

type ClientType = 'web' | 'mobile';

type Auth0ExchangeMobileResponse = {
  token?: string;
};

const AUTH0_EXCHANGE_URL = '/api/authentication/auth0';

const buildExchangeHeaders = (accessToken: string, clientType: ClientType): Record<string, string> => {
  const headers: Record<string, string> = {
    authorization: `Bearer ${accessToken}`,
    'x-forwarded-authorization': `Bearer ${import.meta.env.VITE_API_X_FORWARDED_AUTHORIZATION}`,
    'x-client-type': clientType,
  };

  // Même passerelle Cloudflare Access que placementInstance
  const cfAccessClientId = import.meta.env.VITE_CF_ACCESS_CLIENT_ID;
  const cfAccessClientSecret = import.meta.env.VITE_CF_ACCESS_CLIENT_SECRET;
  if (cfAccessClientId && cfAccessClientSecret) {
    headers['CF-Access-Client-Id'] = cfAccessClientId;
    headers['CF-Access-Client-Secret'] = cfAccessClientSecret;
  }

  return headers;
};

// Échange l'access token Auth0 contre le JWT interne : en web le BFF répond 204 et pose les cookies
// BEARER / refresh_token, en mobile il renvoie les tokens dans le corps.
const exchangeAuth0Token = async (accessToken: string, clientType: ClientType): Promise<Response> => {
  const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}${AUTH0_EXCHANGE_URL}`, {
    method: 'POST',
    headers: buildExchangeHeaders(accessToken, clientType),
    credentials: clientType === 'web' ? 'include' : 'omit',
  });
  if (!response.ok) throw new Error(`Auth0 exchange failed: ${response.status}`);

  return response;
};

// Le parcours KYC lit un JWT dans sessionStorage.authToken : on le récupère en mode mobile avec le même
// access token Auth0, sans bloquer la connexion en cas d'échec.
const fetchKycAuthToken = async (accessToken: string): Promise<void> => {
  try {
    const response = await exchangeAuth0Token(accessToken, 'mobile');
    const data: Auth0ExchangeMobileResponse = await response.json();
    if (data.token) sessionStorage.setItem('authToken', data.token);
  } catch (error) {
    log.error(`useAuth0Login: KYC auth token failed: ${String(error)}`);
  }
};

const trackLoginResult = (result: 'OK' | 'KO') => {
  void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
    event_id: 'clic',
    event_categorie: 'clic',
    event_metier: 'espace client placement',
    event_nom: 'je me connecte',
    event_detail_1: 'connexion',
    event_detail_2: result,
  });
};

const useAuth0Login = () => {
  const { loginWithRedirect, getAccessTokenSilently } = useAuth0();

  // Redirection vers la page Auth0 (identifiant, mot de passe puis code MFA)
  const startLogin = () => loginWithRedirect();

  const completeLogin = async (): Promise<ProfileResponse> => {
    const accessToken = await getAccessTokenSilently();
    if (!accessToken) throw new Error('Missing Auth0 access token');

    void fetchKycAuthToken(accessToken);
    await exchangeAuth0Token(accessToken, 'web');

    return getApiAccountProfile();
  };

  return { startLogin, completeLogin };
};

export default useAuth0Login;
export { trackLoginResult };
